import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { promptTemplatesApi, sitesApi, useAuthStore } from '@wordpuppi/shared';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export const Route = createFileRoute('/templates/shared/$token')({ component: SharedTemplatePage });
export const PENDING_SHARED_TEMPLATE_KEY = 'wpp-pending-shared-template';

function SharedTemplatePage() {
  const { token } = Route.useParams();
  const { isAuthenticated } = useAuthStore();
  const template = useQuery({ queryKey: ['shared-template', token], queryFn: () => promptTemplatesApi.readShared(token), retry: false });
  const sites = useQuery({ queryKey: ['sites', 'list'], queryFn: () => sitesApi.list(), enabled: isAuthenticated });
  const available = sites.data?.data ?? [];
  const [chosen, setChosen] = useState('');
  const siteSlug = chosen || available[0]?.slug;
  const use = () => {
    if (!siteSlug) return;
    // Only the capability token crosses the route. Baxter fetches the prompt and
    // puts it in the ordinary user composer; it never becomes a system message.
    window.location.assign(`/sites/${encodeURIComponent(siteSlug)}?sharedTemplate=${encodeURIComponent(token)}`);
  };
  return <main className="mx-auto flex min-h-screen max-w-2xl items-center p-5">
    <Card className="w-full"><CardHeader><CardTitle>Shared prompt template</CardTitle></CardHeader><CardContent className="space-y-5">
      {template.isLoading && <p>Loading template…</p>}
      {template.isError && <p role="alert">This share link is invalid or has been revoked.</p>}
      {template.data && <>
        <div><h1 className="text-xl font-semibold">{template.data.title}</h1><p className="text-muted-foreground">{template.data.description}</p></div>
        <p>{template.data.about}</p>
        <div className="flex flex-wrap gap-1.5">{template.data.tags.map((tag) => <Badge key={tag} variant="secondary">{tag}</Badge>)}</div>
        <div><h2 className="mb-2 text-sm font-semibold">Prompt text</h2><pre className="whitespace-pre-wrap rounded-lg border bg-muted/25 p-4 font-sans text-sm">{template.data.prompt}</pre></div>
        <p className="text-sm text-muted-foreground">Review this user-provided prompt before sending it to Baxter.</p>
        {!isAuthenticated ? <Button asChild><Link to="/login" onClick={() => sessionStorage.setItem(PENDING_SHARED_TEMPLATE_KEY, token)}>Sign in to use in my site</Link></Button> : <div className="flex flex-wrap items-end gap-3">
          {available.length > 1 && <label className="grid gap-1 text-sm">Site<select aria-label="Choose a site" value={siteSlug} onChange={(e) => setChosen(e.target.value)} className="h-9 rounded-md border bg-background px-3">{available.map((site) => <option key={site.slug} value={site.slug}>{site.name}</option>)}</select></label>}
          {available.length === 0 ? <Button asChild><Link to="/sites">Choose or create a site</Link></Button> : <Button onClick={use}>Use in my site</Button>}
        </div>}
      </>}
    </CardContent></Card>
  </main>;
}
